import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { IconBug, IconShare2, IconRefresh } from "@tabler/icons-react";
import { z } from "zod";
import { displayToast } from "../../components/app/AppToast";
import { ConversationMenu } from "../../components/gpt/ConversationMenu";
import { GptConversationHeader } from "../../components/gpt/GptConversationHeader";
import {
  GPTHistoryButton,
  type GptHistoryReveal,
} from "../../components/gpt/GPTHistoryPanel";
import { AcquisitionSuggestionCard } from "../../components/gpt/chat/AcquisitionSuggestionCard";
import { GptConversation } from "../../components/gpt/chat/GptConversation";
import type { GptAnswerForm } from "../../components/gpt/chat/GPTConversationTabs";
import { gptBackground } from "../../components/gpt/layout";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import Loader from "../../components/Loader";
import Error404Page from "../static/Error404Page";
import { BackButton } from "../../components/ui/BackButton";
import { GptConversationBreadcrumb } from "../../components/gpt/GptConversationBreadcrumb";
import { askedFromSchema } from "../../domains/gpt/askedFrom";
import {
  shownEvidenceCheck,
  UNVERIFIED_EVIDENCE_FLAG,
} from "../../domains/gpt/evidenceCheck";
import { Button } from "../../components/ui/Button";
import { isDesktopShell } from "../../common/hooks/useIsDesktopView";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import { useLocationState } from "../../domains/app/hooks/useLocationState";
import { useHandedQuestionOwnership } from "../../domains/gpt/hooks/useHandedQuestionOwnership";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import {
  assistantOriginSchema,
  type AssistantOrigin,
} from "../../domains/assistant/assistantChatTransport";
import {
  REDACTION_CATEGORIES,
  type Redaction,
} from "../../domains/assistant/redaction";
import { useAssistantChat } from "../../domains/assistant/useAssistantChat";
import { useQuestionRedaction } from "../../domains/assistant/useQuestionRedaction";
import { useChatAttachments } from "../../domains/gpt/hooks/useChatAttachments";
import { useChatKeyboardScroll } from "../../domains/gpt/hooks/useChatKeyboardScroll";
import { useChatReferences } from "../../domains/gpt/hooks/useChatReferences";
import { useChatScrollAnchor } from "../../domains/gpt/hooks/useChatScrollAnchor";
import { useChatSuggestion } from "../../domains/gpt/hooks/useChatSuggestion";
import {
  hasFinishedAnswer,
  useComposerBand,
} from "../../domains/gpt/hooks/useComposerBand";
import { useCopyAnswer } from "../../domains/gpt/hooks/useCopyAnswer";
import { useEnsureGptConversation } from "../../domains/gpt/hooks/useEnsureGptConversation";
import {
  assistantUIMessages,
  uiMessageFollowups,
  uiMessageQuestionCount,
  uiMessageRequest,
  uiMessageSourceTotal,
  type GeneralChatUIMessage,
} from "../../domains/gpt/hooks/useGptChat";
import {
  useDeleteConversation,
  useRenameConversation,
} from "../../domains/gpt/hooks/useManageConversation";
import {
  CONVERSATION_SHARE_FLAG,
  useShareConversation,
  useUnshareConversation,
} from "../../domains/gpt/hooks/useShareConversation";
import { openAnnotation } from "../../domains/gpt/stores/annotationSheetStore";
import { useKeyboardManaged } from "../../hooks/use-keyboard-managed";
import { createMemoryStore } from "../../lib/store";
import {
  apiClient,
  clearApiQuery,
  useApiQuery,
  useApiRevalidate,
  type Api,
} from "../../services/api";
import {
  WizardNote,
  type WizardNoteView,
} from "../../features/juiscigpt-wizard/WizardNote";
import { WizardPauseBanner } from "../../features/juiscigpt-wizard/WizardPauseBanner";
import { useTrackOnMount } from "../../hooks/use-track-on-mount";
import {
  gaEventJuisciGptWizardEnded,
  gaEventJuisciGptWizardStepShown,
  type WizardTrackedContext,
  type WizardTrackedStep,
} from "../../tools/analytics/domains/juiscigptWizard.analytics";
import {
  useWizardNoteCollapsed,
  useWizardSession,
} from "../../features/juiscigpt-wizard/wizardSession";
import {
  type WizardAnswerStage,
  wizardAnswerStage,
} from "../../features/juiscigpt-wizard/wizardAnswerStage";
import {
  gaEventGptInteraction,
  gaEventSearchGpt,
} from "../../tools/analytics/domains/gpt.analytics";
import { AssistantDebugDrawer } from "./assistant-debug/AssistantDebugDrawer";
import { ASSISTANT_DEBUG_FLAG } from "../../../../api/src/shared/growthbook-flags";

// Same Back target as the legacy page: the landing, with the history revealed.
const HISTORY_ARRIVAL_REVEALING = { historyArrival: "revealing" } as const;

type EvidenceChecks =
  Api["get"]["/assistant/conversations/:conversationId/evidence-checks"]["output"];

// The route reads 50 runs at most: an older turn shows no check.
const EVIDENCE_CHECK_RUN_LIMIT = 50;

// A check takes 15 to 40 seconds; polling stops once none is pending.
function evidenceCheckInterval(
  data: EvidenceChecks | undefined
): number | false {
  return data !== undefined &&
    data.body.checks.some((check) => check.state.status === "pending")
    ? 5_000
    : false;
}

// What the sender's Redaction found, which a second run no longer sees.
const noticeSchema = z
  .object({
    summary: z.object({
      matchCount: z.number().int().positive(),
      categories: z.array(z.enum(REDACTION_CATEGORIES)).min(1),
    }),
  })
  .nullable()
  .default(null);

const locationStateSchema = z.object({
  initialQuestionOwnerUid: z.string().nullable().default(null),
  // The question the landing handed over, asked on arrival. Already redacted there.
  pending: z
    .object({
      question: z.string().min(1),
      origin: assistantOriginSchema,
      requestId: z.uuid(),
      notice: noticeSchema,
    })
    .nullable()
    .default(null),
  // The Notice of a question that moved here from another conversation, already in this one.
  notice: noticeSchema,
  // The page the question was asked from, which the breadcrumb leads back to.
  askedFrom: askedFromSchema,
});

type PendingQuestion = z.infer<typeof locationStateSchema>["pending"];

// Where a checked Question goes. The page resolves it at delivery.
type SendRoute =
  | { kind: "composer" }
  | { kind: "card"; toolCallId: string }
  | { kind: "followUp" }
  | { kind: "handedOver"; origin: AssistantOrigin; requestId: string };

const useDebugDrawerOpen = createMemoryStore(false);

const WIZARD_PATH = "/webapp/private/users/me/juiscigpt-wizard";

// Note 1 follows the answer of the tour question; note 2 waits for it to be
// complete (docs/juiscigpt-wizard.md REQ-06, REQ-07). A stopped run shows no
// note: an error never starts the tour again (REQ-18).
function trackedStepOfNote(view: WizardNoteView): WizardTrackedStep {
  switch (view.note) {
    case 1:
      switch (view.screen) {
        case 1:
          return "note_1_search";
        case 2:
          return "note_1_sort";
        case 3:
          return "note_1_write";
        default:
          throw new Error(view satisfies never);
      }
    case 2:
      return "note_2";
    case 3:
      return "note_3";
    default:
      throw new Error(view satisfies never);
  }
}

function wizardNoteOfStage(stage: WizardAnswerStage): WizardNoteView | null {
  switch (stage.kind) {
    case "searching":
      return { note: 1, screen: 1 };
    case "reading":
      return {
        note: 1,
        screen: 2,
        readCount: stage.readCount,
        searchCounts: stage.searchCounts,
      };
    case "writing":
      return { note: 1, screen: 3 };
    case "answered":
      return { note: 2 };
    case "stopped":
      return null;
    default:
      throw new Error(stage satisfies never);
  }
}

// A JuisciGPT conversation on the assistant engine. The drawer and its card
// belong to `GPTConversationPage`, which dispatches here on a UUID id.
export function AssistantConversationPage({
  id,
  roomId,
  historyReveal,
}: {
  id: string;
  roomId: string | null;
  historyReveal: GptHistoryReveal;
}): React.ReactElement {
  useKeyboardManaged();
  const { t } = useTranslation();
  const { gptNewChatPageLink } = usePageLinks(roomId);
  const roomQuery = useApiQuery(
    "/webapp/private/rooms/:id",
    roomId !== null ? { param: { id: roomId } } : false
  );
  const isRoomLoading = roomQuery.state === "pending" && roomQuery.isFetching;
  const navigationState = useLocationState(locationStateSchema);
  const { initialQuestionOwnerUid } = navigationState;
  const history = useHistory();
  const questionOwnership = useHandedQuestionOwnership(
    initialQuestionOwnerUid,
    navigationState.pending
  );
  const handedPending = questionOwnership.question;
  const pending = questionOwnership.isOwner() ? handedPending : null;
  const conversationQuery = useApiQuery(
    "/assistant/conversations/:conversationId",
    { param: { conversationId: id } }
  );

  const isHandedQuestionPersisted =
    initialQuestionOwnerUid !== null &&
    handedPending !== null &&
    conversationQuery.state === "success" &&
    conversationQuery.body.messages.some(
      (message) => message.id === handedPending.requestId
    );
  useEffectOnce(() => {
    if (!isHandedQuestionPersisted || !questionOwnership.isOwner()) return;
    const { pathname, search, hash } = history.location;
    history.replace({
      pathname,
      search,
      hash,
      state: {
        ...navigationState,
        pending: null,
        initialQuestionOwnerUid: null,
      },
    });
  }, [isHandedQuestionPersisted]);

  if (roomId !== null && isRoomLoading) return <Loader expanded />;
  if (conversationQuery.state === "error" && conversationQuery.status === 404)
    return <Error404Page />;

  // The landing seeds the new conversation, so only a cold entry waits here.
  if (conversationQuery.state !== "success") {
    return (
      <ScrollableLayout
        containerStyle={{ background: gptBackground() }}
        background={gptBackground()}
        headerClassName="bg-white/90 backdrop-blur-md"
        header={
          <GptConversationHeader
            title={null}
            onBack={null}
            visualAbstracts={null}
            newConversationTo={gptNewChatPageLink()}
            actions={null}
          />
        }
      >
        {conversationQuery.state === "pending" ? (
          <Loader expanded />
        ) : (
          <div className="mx-5 mt-8 flex flex-col items-start gap-2">
            <p className="my-0 text-sm text-secondary-raspberry-1">
              {t("Something went wrong. Please try again.")}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              leftIcon={<IconRefresh className="size-4" stroke={1.5} />}
              onClick={() => {
                void conversationQuery.refetch();
              }}
            >
              {t("Retry")}
            </Button>
          </div>
        )}
      </ScrollableLayout>
    );
  }

  const initialMessages = assistantUIMessages(conversationQuery.body.messages);
  // A restored entry whose question is already in the transcript resumes it.
  // The question's message id is its request id; a fork's shared turns are not.
  const ask =
    pending !== null &&
    initialMessages.every((message) => message.id !== pending.requestId)
      ? pending
      : null;

  return (
    <AssistantConversation
      key={id}
      id={id}
      roomId={roomId}
      historyReveal={historyReveal}
      title={conversationQuery.body.title}
      isShared={conversationQuery.body.isShared}
      initialMessages={initialMessages}
      ask={ask}
      isPendingQuestionOwner={questionOwnership.isOwner}
    />
  );
}

function AssistantConversation({
  id,
  roomId,
  historyReveal,
  title,
  isShared,
  initialMessages,
  ask,
  isPendingQuestionOwner,
}: {
  id: string;
  roomId: string | null;
  historyReveal: GptHistoryReveal;
  // Null until the first run names the conversation.
  title: string | null;
  // A public link shows the conversation: the menu offers Stop sharing.
  isShared: boolean;
  // Read once: they seed the chat at mount.
  initialMessages: GeneralChatUIMessage[];
  // The handed-over question to send at mount, or null to resume.
  ask: PendingQuestion;
  isPendingQuestionOwner: () => boolean;
}): React.ReactElement {
  const { t } = useTranslation();
  const history = useHistory();
  const { gptNewChatPageLink, gptConversationPageLink, articleLink } =
    usePageLinks(roomId);
  const { askedFrom, notice } = useLocationState(locationStateSchema);
  const isShareEnabled = useFeatureIsOn(CONVERSATION_SHARE_FLAG);
  const isDebugEnabled = useFeatureIsOn(ASSISTANT_DEBUG_FLAG);
  const isUnverifiedShown = useFeatureIsOn(UNVERIFIED_EVIDENCE_FLAG);
  const { scrollRef, isAtBottom, scrollToBottom, onScroll } =
    useChatScrollAnchor({ initialToBottom: true });
  const { paddingBottom: keyboardPaddingBottom } =
    useChatKeyboardScroll(scrollToBottom);
  const isNewConversation =
    new URLSearchParams(history.location.search).get("new") !== null;
  const copyAnswer = useCopyAnswer();
  const shareConversation = useShareConversation();
  const unshareConversation = useUnshareConversation();
  const renameConversation = useRenameConversation();
  const deleteConversation = useDeleteConversation();
  const revalidate = useApiRevalidate();
  const conversation = useEnsureGptConversation({
    conversationId: id,
    engine: "assistant",
  });
  const attachments = useChatAttachments({ conversation });
  const references = useChatReferences({ conversation });
  // A conversation that references one article and nothing else is about that
  // study (Figma "D2.conversation.about-study"). Same query as the chips', so
  // one request serves both.
  // "About this study" is the desktop's (the phone keeps its reference list).
  const referencesQuery = useApiQuery(
    "/assistant/conversations/:conversationId/references",
    isDesktopShell() ? { param: { conversationId: id } } : false
  );
  const study = (() => {
    if (referencesQuery.state !== "success") return null;
    const rows = referencesQuery.body.references;
    const only = rows.at(0);
    if (rows.length !== 1 || only === undefined) return null;
    if (only.kind !== "article" || only.slug === null) return null;
    return {
      title: only.title,
      journal: only.journal,
      year: only.year,
      link: articleLink({ slug: only.slug }),
    };
  })();
  const assistant = useAssistantChat({
    conversationId: id,
    initialMessages,
    resume: ask === null,
    // The list shows a conversation while its first answer is being written.
    onTurnAccepted: () => {
      void revalidate("/assistant/conversations");
    },
    // The new conversation holds the question; its page resumes the run.
    onTurnMoved: (conversationId, movedNotice) => {
      // A reader who left during the POST stays there; the revalidated list shows the new conversation.
      // The route matches its path as a prefix, a trailing slash included.
      const here = gptConversationPageLink(id);
      const { pathname } = history.location;
      if (pathname !== here && !pathname.startsWith(`${here}/`)) return;
      history.replace(
        {
          pathname: gptConversationPageLink(conversationId),
          search: history.location.search,
        },
        { askedFrom, notice: movedNotice }
      );
    },
    onTurnFinish: () => {
      void revalidate("/assistant/conversations/:conversationId", {
        param: { conversationId: id },
      });
      void revalidate("/assistant/conversations");
      // Removed rather than revalidated, so the next landing waits for the new subject.
      clearApiQuery("/assistant/latest-topic", {});
    },
    // The text stays on the device as an unsent message, to edit or retry.
    // An error of the assistant engine ends the tour of its question (REQ-18,
    // OD-03): the normal error shows, and Settings starts the tour again.
    onTurnFailed: () => {
      if (
        wizard === null ||
        wizard.status !== "running" ||
        wizard.conversationId !== id
      )
        return;
      void changeWizard(
        () =>
          apiClient.post(`${WIZARD_PATH}/end`, { json: { reason: "error" } }),
        () => {
          if (wizardContext === null) return;
          // A forced exit, counted apart from the other exits (REQ-27). The
          // server keeps the three screens of note 1 under one step: the
          // answer's parts say which one the run reached.
          const reached = wizardAnswerStage({
            messages: chat.messages,
            status: "streaming",
          });
          gaEventJuisciGptWizardEnded({
            reason: "error",
            step:
              wizard.step === "note_3"
                ? "note_3"
                : reached.kind === "writing"
                  ? "note_1_write"
                  : reached.kind === "reading"
                    ? "note_1_sort"
                    : "note_1_search",
            ...wizardContext,
          });
        }
      );
    },
    onRedactionRefused: () => questionRedaction.captureRefusal(),
    initialNotice: notice,
  });
  const chat = assistant.chat;
  // The JuisciGPT wizard on the conversation of its first question, on mobile
  // and on desktop (REQ-22). The trial happens on the home.
  const wizardQuery = useApiQuery(WIZARD_PATH, {});
  const wizard = wizardQuery.state === "success" ? wizardQuery.body : null;
  const [wizardSession, setWizardSession] = useWizardSession();
  // The running tour of this conversation, at a note or at the trial.
  const tourHere =
    wizard === null ||
    wizard.status !== "running" ||
    wizard.conversationId !== id
      ? null
      : wizard;
  const wizardStage = wizardAnswerStage(chat);
  // The notes show in the session that started or resumed the tour; any
  // other session, or "Quitter la visite", shows the pause banner (REQ-17).
  const wizardNote: WizardNoteView | null =
    tourHere === null ||
    tourHere.step === "trial" ||
    wizardSession.tour !== "active"
      ? null
      : tourHere.step === "note_3"
        ? { note: 3 }
        : wizardNoteOfStage(wizardStage);
  const wizardPause =
    tourHere === null || wizardSession.tour === "active" ? null : (
      <WizardPauseBanner
        note={
          tourHere.step !== "note_1"
            ? 3
            : wizardStage.kind === "answered"
              ? 2
              : 1
        }
        onResume={() => {
          setWizardSession((session) => ({ ...session, tour: "active" }));
          // The trial lives on the JuisciGPT home (REQ-08).
          if (tourHere.step === "trial") history.push(gptNewChatPageLink());
        }}
        onEnd={() => {
          void changeWizard(
            () =>
              apiClient.post(`${WIZARD_PATH}/end`, {
                json: { reason: "skipped" },
              }),
            () => {
              gaEventJuisciGptWizardEnded({
                reason: "skipped",
                step: "pause",
                gate: tourHere.gate,
                questionLevel:
                  tourHere.question === null ? null : tourHere.question.level,
              });
              displayToast(
                t("Tour set aside · find it again in Settings › Help"),
                "info"
              );
            }
          );
        }}
      />
    );

  // The context of every wizard event (REQ-28), and the step on screen
  // (REQ-26): each screen of note 1, notes 2 and 3, or the pause.
  const wizardContext: WizardTrackedContext | null =
    tourHere === null
      ? null
      : {
          gate: tourHere.gate,
          questionLevel:
            tourHere.question === null ? null : tourHere.question.level,
        };
  // A folded note shows its bar, not its screen: it counts once it opens.
  const [isNoteFolded] = useWizardNoteCollapsed();
  const wizardStep: WizardTrackedStep | null =
    wizardNote !== null
      ? isNoteFolded
        ? null
        : trackedStepOfNote(wizardNote)
      : wizardPause !== null
        ? "pause"
        : null;
  useTrackOnMount(
    "juiscigpt-wizard-step-shown",
    wizardStep === null || wizardContext === null
      ? { status: "hidden" }
      : { status: "shown", key: wizardStep },
    () => {
      if (wizardStep === null || wizardContext === null)
        throw new Error("should not happen: a step without its tour");
      gaEventJuisciGptWizardStepShown({ step: wizardStep, ...wizardContext });
    }
  );

  async function changeWizard(
    write: () => Promise<unknown>,
    after: () => void
  ): Promise<void> {
    try {
      await write();
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
      return;
    }
    await revalidate(WIZARD_PATH);
    after();
  }
  const questionRedaction = useQuestionRedaction({
    enabled: true,
    deliver,
    failedTextPlace: {
      kind: "transcript",
      // A fork's shared Questions included: they are on screen too.
      questionCount: chat.messages.reduce(
        (count, message) => count + uiMessageQuestionCount(message),
        0
      ),
    },
  });
  // A shared turn has no run.
  const ownMessages = chat.messages.filter(
    (message) =>
      message.metadata === undefined || message.metadata.runId !== null
  );
  const questionCount = ownMessages.filter(
    (message) => message.role === "user"
  ).length;
  // The analytics rank a question in the transcript the reader sees: a
  // continued legacy conversation's copied turns count, so its first question
  // here is not number 1.
  const transcriptQuestionCount = chat.messages.filter(
    (message) => message.role === "user"
  ).length;

  const band = useComposerBand(
    useChatSuggestion(chat, roomId !== null, "assistant"),
    questionCount,
    hasFinishedAnswer(ownMessages, chat.status)
  );

  const lastMessage = chat.messages.at(-1);
  const lastAnswer =
    lastMessage !== undefined && lastMessage.role === "assistant"
      ? lastMessage
      : null;
  // A shared turn carried into a fork has no run to regenerate.
  const lastRunId =
    lastAnswer === null || lastAnswer.metadata === undefined
      ? null
      : lastAnswer.metadata.runId;
  // The composer's next send answers it.
  const pendingCard = lastAnswer === null ? null : uiMessageRequest(lastAnswer);

  // An annotation judges an answer by its run.
  const annotationsQuery = useApiQuery(
    "/assistant/conversations/:conversationId/annotations",
    { param: { conversationId: id } }
  );
  const ownVerdicts =
    annotationsQuery.state === "success"
      ? annotationsQuery.body.annotations.filter(
          (annotation) => annotation.origin === "juisci"
        )
      : [];
  // `GptConversation` numbers a turn by the ordinal of its assistant message.
  const turnRunIds = chat.messages.flatMap((message) =>
    message.role === "assistant"
      ? [message.metadata === undefined ? null : message.metadata.runId]
      : []
  );
  // A check starts with its published answer, whose total arrives while the stream still waits for the turn suggestion.
  const checkedRunIds = chat.messages
    .flatMap((message) =>
      message.role === "assistant" &&
      message.metadata !== undefined &&
      message.metadata.runId !== null &&
      uiMessageSourceTotal(message) !== null
        ? [message.metadata.runId]
        : []
    )
    .slice(-EVIDENCE_CHECK_RUN_LIMIT);
  const evidenceChecksQuery = useApiQuery(
    "/assistant/conversations/:conversationId/evidence-checks",
    checkedRunIds.length === 0
      ? false
      : {
          param: { conversationId: id },
          query: { runIds: checkedRunIds },
          refetchInterval: evidenceCheckInterval,
          staleTime: 0,
          // A new turn changes the key: the shown checks stay while it loads.
          keepPreviousData: true,
        }
  );
  const evidenceCheckByRunId = new Map(
    evidenceChecksQuery.state === "success"
      ? evidenceChecksQuery.body.checks.flatMap((check) => {
          const shown = shownEvidenceCheck(check.state, isUnverifiedShown);
          return shown === null ? [] : [[check.runId, shown] as const];
        })
      : []
  );

  function submit(
    redaction: Redaction,
    origin: AssistantOrigin,
    requestId: string
  ): void {
    if (redaction.text === "") return;
    // Follow-up chips call this directly, bypassing the composer's own guard.
    if (chat.status === "submitted" || chat.status === "streaming") return;
    assistant.send(requestId, redaction, origin);
    // Every question but the first one of the tour conversation ends a
    // running tour (REQ-09). The first one starts it whatever its origin: the
    // tour question, an adapted one, or the reader's own after "Plus tard".
    if (
      wizard !== null &&
      wizard.status === "running" &&
      !(wizard.conversationId === id && questionCount === 0)
    ) {
      // The context comes from the running tour, not from this page: the
      // second question can come from another conversation.
      const tour = wizard;
      void changeWizard(
        () =>
          apiClient.post(`${WIZARD_PATH}/end`, {
            json: { reason: "completed" },
          }),
        () => {
          // The second question: the tour is complete (REQ-09).
          gaEventJuisciGptWizardEnded({
            reason: "completed",
            step:
              wizardStep !== null
                ? wizardStep
                : tour.step === "trial"
                  ? "trial"
                  : "pause",
            gate: tour.gate,
            questionLevel:
              tour.question === null ? null : tour.question.level,
          });
          displayToast(
            t("Tour finished · find it again in Settings › Help"),
            "success"
          );
        }
      );
    }
    // The assistant engine has no mode picker: the event reports no mode.
    gaEventSearchGpt({
      engine: "assistant",
      isNewConversation,
      origin,
      questionCount: transcriptQuestionCount + 1,
      question: redaction.text,
    });
  }

  // Resolved with the state of the render that delivers, a retry included (AC-15).
  function deliver(redaction: Redaction, route: SendRoute): void {
    switch (route.kind) {
      // An open card takes the composer's next Question as its answer.
      case "composer":
        if (pendingCard === null)
          submit(redaction, "typed", crypto.randomUUID());
        else assistant.answer(pendingCard.toolCallId, redaction);
        return;
      case "card":
        // A card closed since a failed send leaves its text to the composer rule.
        if (pendingCard !== null && pendingCard.toolCallId === route.toolCallId)
          assistant.answer(route.toolCallId, redaction);
        else deliver(redaction, { kind: "composer" });
        return;
      case "followUp":
        submit(redaction, "followup_suggestion", crypto.randomUUID());
        return;
      case "handedOver":
        submit(redaction, route.origin, route.requestId);
        return;
      default:
        throw new Error(route satisfies never);
    }
  }

  // The parent passes `ask` null once the transcript holds the question.
  useEffectOnce(() => {
    if (!isPendingQuestionOwner() || ask === null) return;
    questionRedaction.sendHandedOver(ask.question, ask.notice, {
      kind: "handedOver",
      origin: ask.origin,
      requestId: ask.requestId,
    });
  });

  function toastFailure(): void {
    displayToast(t("Something went wrong. Please try again."), "error");
  }

  function handleCopyAnswer(
    question: string,
    text: string,
    answerForm: GptAnswerForm
  ): void {
    copyAnswer(question, text, () => {
      gaEventGptInteraction({
        engine: "assistant",
        isNewConversation,
        isSuggestedQuestion: false,
        questionCount: transcriptQuestionCount,
        interaction: "copy_answer",
        answerForm,
      });
      displayToast(
        isDesktopShell() ? t("Answer copied") : t("Copied"),
        "success"
      );
    });
  }

  function comingSoon(): void {
    displayToast(t("Coming soon"), "info");
  }

  function handleBack(): void {
    history.replace(gptNewChatPageLink(), HISTORY_ARRIVAL_REVEALING);
  }

  async function handleDelete(): Promise<boolean> {
    const deleted = await deleteConversation(id);
    if (deleted === null) return false;
    handleBack();
    return true;
  }

  const shareThisConversation = isShareEnabled
    ? () => {
        void shareConversation(id, questionCount);
      }
    : null;

  const [isDebugOpen, setDebugOpen] = useDebugDrawerOpen();

  const conversationMenu = (
    <ConversationMenu
      title={title}
      onShare={shareThisConversation}
      onUnshare={
        isShareEnabled && isShared
          ? () => {
              void unshareConversation(id);
            }
          : null
      }
      // The phone's header has no room for it beside the title: Debug rides the menu.
      onDebug={
        isDebugEnabled
          ? () => {
              setDebugOpen(true);
            }
          : null
      }
      onRename={(nextTitle) => renameConversation(id, nextTitle)}
      onDelete={handleDelete}
      className={null}
    />
  );

  // The desktop top bar carries no owner menu, so Debug keeps its own button there.
  const desktopDebugButton = isDebugEnabled ? (
    <Button
      type="button"
      variant="outline"
      size="sm"
      leftIcon={<IconBug className="size-4" stroke={1.5} />}
      onClick={() => {
        setDebugOpen(true);
      }}
    >
      Debug
    </Button>
  ) : null;

  const desktopLeading = isDesktopShell() ? (
    <GptConversationBreadcrumb
      askedFrom={askedFrom}
      newChatLink={gptNewChatPageLink()}
      // Back lands on the JuisciGPT landing, which the first crumb names.
      onBackToGpt={handleBack}
      // Named server-side from the first question, so null until it has landed.
      current={
        study !== null
          ? t("About this study")
          : title === null
            ? t("New conversation")
            : title
      }
    />
  ) : (
    <>
      <BackButton
        onClick={handleBack}
        className="rounded-full bg-white/80 px-3 py-1.5 shadow-[0px_2px_8px_rgba(0,0,0,0.08)] backdrop-blur-md hover:bg-white"
      >
        {t("Back")}
      </BackButton>
      <GPTHistoryButton
        onClick={
          historyReveal.isOpen ? historyReveal.close : historyReveal.open
        }
        className="ml-1"
      />
    </>
  );

  return (
    <ScrollableLayout
      background={gptBackground()}
      headerClassName="bg-white/90 backdrop-blur-md"
      header={
        <GptConversationHeader
          title={title}
          onBack={handleBack}
          visualAbstracts={null}
          newConversationTo={gptNewChatPageLink()}
          actions={<>{conversationMenu}</>}
        />
      }
      scrollRef={scrollRef}
      onScroll={onScroll}
      style={{
        ...historyReveal.scrollLockStyle,
        paddingBottom: keyboardPaddingBottom,
      }}
      hideScrollbar
    >
      <GptConversation
        chat={chat}
        leading={desktopLeading}
        headerActions={
          <>
            {isDesktopShell() ? desktopDebugButton : null}
            {/* No owner menu in the desktop top bar (Figma): the sidebar's
                Recent rows rename, delete and stop sharing. Share sits here
                too, so a conversation shares from where it is read. */}
            {isDesktopShell() && shareThisConversation !== null ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                leftIcon={<IconShare2 className="size-4" stroke={1.5} />}
                onClick={shareThisConversation}
              >
                {t("Share")}
              </Button>
            ) : null}
            {isDesktopShell() ? null : conversationMenu}
          </>
        }
        composerNotice={
          wizardPause !== null ? (
            wizardPause
          ) : band.showAcquisitionAsk ? (
            <AcquisitionSuggestionCard />
          ) : null
        }
        afterTurns={null}
        cwyopPaper={null}
        showModePicker={false}
        // The assistant engine writes no Evidence or Search turn.
        evidenceGraphByTurnIndex={new Map()}
        evidenceCheckByRunId={evidenceCheckByRunId}
        onOpenEvidenceSource={() => {
          throw new Error(
            "should not happen: an Evidence turn on the assistant engine"
          );
        }}
        pendingEvidence={null}
        searchByTurnIndex={new Map()}
        pendingSearch={null}
        onOpenSearchArticle={() => {
          throw new Error(
            "should not happen: a Search turn on the assistant engine"
          );
        }}
        onRefineSearch={() => {
          throw new Error(
            "should not happen: a Search turn on the assistant engine"
          );
        }}
        onAskGeneral={null}
        onAnswerCard={(toolCallId, text) => {
          questionRedaction.sendRedacted(text, { kind: "card", toolCallId });
        }}
        answerMath={false}
        actions={{
          onCopy: handleCopyAnswer,
          onShare:
            shareThisConversation === null ? comingSoon : shareThisConversation,
          showShare: isShareEnabled,
          onRegenerate:
            lastRunId === null
              ? null
              : () => {
                  assistant.regenerate(lastRunId);
                },
          onRetry: () => {
            // The retry reports its own failures.
            void assistant.retry();
          },
          onEvaluate: (turn) => {
            const runId = turnRunIds[turn.turnIndex];
            if (runId === undefined)
              throw new Error(
                `should not happen: turn ${String(turn.turnIndex)} has no message`
              );
            // A shared turn carried into a fork was answered in another conversation.
            if (runId === null) {
              displayToast(
                t("An answer from a shared conversation cannot be evaluated."),
                "warning"
              );
              return;
            }
            const existing = ownVerdicts.find(
              (annotation) => annotation.runId === runId
            );
            openAnnotation({
              subject: { engine: "assistant", conversationId: id, runId },
              question: turn.question,
              answer: turn.answer,
              existing: existing === undefined ? null : existing.verdict,
            });
          },
          evaluatedTurnIndexes: turnRunIds.flatMap((runId, index) =>
            runId !== null &&
            ownVerdicts.some((annotation) => annotation.runId === runId)
              ? [index]
              : []
          ),
          comingSoon,
        }}
        followUps={{
          enabled: true,
          questions: lastAnswer === null ? [] : uiMessageFollowups(lastAnswer),
          onPick: (question) => {
            questionRedaction.sendRedacted(question, { kind: "followUp" });
          },
        }}
        disclaimer={t(
          "AI can make mistakes. Consider checking Source information."
        )}
        background={gptBackground()}
        roomId={roomId}
        assistantConversationId={id}
        viewStateKey={id}
        onOpenSources={(answerForm) => {
          gaEventGptInteraction({
            engine: "assistant",
            isNewConversation,
            isSuggestedQuestion: false,
            questionCount: transcriptQuestionCount,
            interaction: "open_reference",
            answerForm,
          });
        }}
        onOpenSource={(answerForm) => {
          gaEventGptInteraction({
            engine: "assistant",
            isNewConversation,
            isSuggestedQuestion: false,
            questionCount: transcriptQuestionCount,
            interaction: "open_source",
            answerForm,
          });
        }}
        isReconnecting={assistant.isReconnecting}
        // The failure of a turn shows as the chat's error.
        hasLostTurn={false}
        followKeyboard={!historyReveal.isOpen}
        composer={{
          kind: "assistant",
          onSubmit: (question) => {
            questionRedaction.sendRedacted(question, { kind: "composer" });
          },
          onStop: () => {
            assistant.stop().catch(toastFailure);
          },
          placeholder:
            pendingCard !== null
              ? t("Your answer…")
              : isDesktopShell()
                ? t("Describe a case, a decision, or a clinical doubt…")
                : t("Keep asking..."),
          // The Notice of the last send replaces the rule (AD-18).
          banner:
            assistant.notice !== null
              ? {
                  kind: "notice",
                  notice: assistant.notice,
                  onDismiss: assistant.dismissNotice,
                }
              : questionRedaction.showInputRule
                ? {
                    kind: "inputRule",
                    onDismiss: questionRedaction.dismissInputRule,
                  }
                : null,
          unsent: questionRedaction.unsent,
          overlapsMatch: questionRedaction.overlapsMatch,
        }}
        scrollRef={scrollRef}
        isAtBottom={isAtBottom}
        onScrollToBottom={scrollToBottom}
        revealAssistantId={null}
        suggestion={band.suggestion}
        pushOptIn={band.showPushOptIn ? { onDone: () => {} } : null}
        attachments={attachments}
        canModifyAttachments
        references={references}
        study={study}
        canModifyReferences
      />
      {isDebugEnabled && (
        <AssistantDebugDrawer
          conversationId={id}
          messages={chat.messages}
          isOpen={isDebugOpen}
          onClose={() => {
            setDebugOpen(false);
          }}
        />
      )}
      {wizardNote !== null ? (
        <WizardNote
          view={wizardNote}
          layout={isDesktopShell() ? "desktop" : "mobile"}
          onNext={() => {
            void changeWizard(
              () =>
                apiClient.post(`${WIZARD_PATH}/step`, {
                  json: { step: "note_3" },
                }),
              () => {}
            );
          }}
          onAsk={() => {
            void changeWizard(
              () =>
                apiClient.post(`${WIZARD_PATH}/step`, {
                  json: { step: "trial" },
                }),
              () => {
                history.push(gptNewChatPageLink());
              }
            );
          }}
          // "Quitter la visite" pauses the tour: the banner above the
          // composer resumes it or ends it (Figma C1).
          onQuit={() => {
            setWizardSession((session) => ({ ...session, tour: "paused" }));
          }}
        />
      ) : null}
    </ScrollableLayout>
  );
}
